import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { AcxExpandableText } from "components/UI/AcxExpandableText";
import moment from "moment";
import type { LLMClassifierApprovalEventSummary } from "models/LLMs/LLMClassifierApproval";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import {
    APPROVAL_EVENT_ICONS,
    approvalEventLabel,
} from "../../utils/llmClassifierApproval";

export const ChangeRecordDialog = observer(function (props: {
    isOpen: boolean;
    onClose: () => void;
    implementationId: string;
    versionLabel?: string;
}) {
    const { isOpen, onClose, implementationId, versionLabel } = props;
    const store = useStore(LLMClassifierBuilderStore);
    // Undefined until loaded; a null list means the load failed.
    const [records, setRecords] = useState<
        LLMClassifierApprovalEventSummary[] | null
    >();
    const loading = store.getTaskLoading(
        LLMClassifierBuilderStore.Tasks.LoadChangeRecord(implementationId),
    );

    useEffect(() => {
        if (!isOpen) return;
        let isCurrent = true;
        setRecords(undefined);
        void store.loadChangeRecord(implementationId).then((events) => {
            // Ignore a slow response for a version the dialog has moved on from.
            if (isCurrent) setRecords(events ?? null);
        });
        return () => {
            isCurrent = false;
        };
    }, [isOpen, implementationId, store]);

    const renderContent = () => {
        if (loading || records === undefined) {
            return (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={28} />
                </Box>
            );
        }
        if (records === null) {
            return (
                <Typography color="error.main" py={2}>
                    The change record couldn't be loaded. Close and reopen it to
                    try again.
                </Typography>
            );
        }
        if (records.length === 0) {
            return (
                <Typography color="gray.500" py={2}>
                    No approval activity has been recorded for this version yet.
                </Typography>
            );
        }
        return <ChangeRecordTable records={records} />;
    };

    return (
        <AcxDialog
            isOpen={isOpen}
            onClose={onClose}
            title="Change record"
            subTitle={versionLabel ? `Approval history for ${versionLabel}` : undefined}
            maxWidth="md"
            fullWidth
            hideCancelButton
            dialogContentChildren={renderContent()}
        />
    );
});

function ChangeRecordTable(props: { records: LLMClassifierApprovalEventSummary[] }) {
    const { records } = props;
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontSize: "12px" }}>Type</TableCell>
                    <TableCell sx={{ fontSize: "12px" }}>Date</TableCell>
                    <TableCell sx={{ fontSize: "12px" }}>Actor</TableCell>
                    <TableCell sx={{ fontSize: "12px", maxWidth: 280 }}>
                        Comment
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {records.map((record) => {
                    const { Icon, color } = APPROVAL_EVENT_ICONS[record.event];
                    return (
                        <TableRow key={record.id}>
                            <TableCell>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Icon
                                        sx={{
                                            color,
                                            fontSize: 16,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography variant="caption">
                                        {approvalEventLabel(record.event)}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    variant="caption"
                                    color="gray.600"
                                    whiteSpace="nowrap"
                                >
                                    {moment
                                        .utc(record.createdOn)
                                        .local()
                                        .format("MMM D · HH:mm")}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="caption" color="gray.600">
                                    {record.createdByName ||
                                        record.createdBy ||
                                        "—"}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                                {record.comment ? (
                                    <AcxExpandableText
                                        variant="caption"
                                        color="gray.600"
                                        maxTruncatedLines={2}
                                    >
                                        {record.comment}
                                    </AcxExpandableText>
                                ) : (
                                    <Typography variant="caption" color="gray.600">
                                        —
                                    </Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}
